import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addContact, editContact } from '../../../../api/endpoints/cmsSite';
import type { SiteContact } from '../../../../api/types/cmsSite';
import { FormDialog } from '../../../../components/common';
import { PhoneField } from '../../../../components/PhoneField';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  phone: z.string().optional(),
  whatsApp: z.string().optional(),
  web: z.string().optional(),
  email: z.string().optional(),
  address: z.string().optional(),
  lat: z.string().optional(),
  lng: z.string().optional(),
  keywords: z.string().optional(),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  title: '',
  phone: '',
  whatsApp: '',
  web: '',
  email: '',
  address: '',
  lat: '',
  lng: '',
  keywords: '',
  description: '',
};

function toFormValues(contact: SiteContact): FormValues {
  return {
    title: contact.title ?? '',
    phone: contact.phone ?? '',
    whatsApp: contact.whatsApp ?? '',
    web: contact.web ?? '',
    email: contact.email ?? '',
    address: contact.address ?? '',
    lat: contact.lat ?? '',
    lng: contact.lng ?? '',
    keywords: contact.keywords ?? '',
    description: contact.description ?? '',
  };
}

/** Create/edit dialog for İletişim (CMS Site Contact — not to be confused with the
 *  unrelated booking-side `Contact` type). Confirmed via local-backend swagger + live curl
 *  (see api/types/cmsSite.ts header). Always rendered inside a specific site's tab, so
 *  `site_Id` is injected from the `siteId` prop. */
export function ContactFormDialog({
  open,
  contact,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  contact: SiteContact | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!contact;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(contact ? toFormValues(contact) : emptyValues);
  }, [open, contact, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && contact
        ? editContact({ id: contact.id, site_Id: siteId, ...values })
        : addContact({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'İletişim kaydı güncellendi.' : 'İletişim kaydı oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'contacts'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'İletişim Kaydını Güncelle' : 'Yeni İletişim'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneField value={field.value} onChange={field.onChange} onBlur={field.onBlur} label="Telefon" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="whatsApp"
                render={({ field }) => (
                  <PhoneField value={field.value} onChange={field.onChange} onBlur={field.onBlur} label="WhatsApp" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Web" {...register('web')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="E-posta" {...register('email')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Adres" {...register('address')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Enlem (Lat)" {...register('lat')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Boylam (Lng)" {...register('lng')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Anahtar Kelimeler" {...register('keywords')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={3} label="Açıklama" {...register('description')} />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
